/**
 * O relatório de live como página HTML autocontida — o template v2
 * ("Relatório Pós Live"), seção a seção, com a copy fixa do template e só os
 * dados, a empresa e a live variando.
 *
 * Uma string, sem React: é o MESMO arquivo que a prévia mostra (iframe
 * `srcdoc`), que o botão "Baixar HTML" salva e que a impressão do navegador
 * vira PDF. Não existe a hipótese de o arquivo sair diferente do que a pessoa
 * viu.
 *
 * PDF: `@media print` reduz a página (zoom) para caber no A4 retrato sem
 * refazer o layout, força a impressão das cores de fundo e evita quebrar card,
 * linha de tabela e título no meio. Tooltips (hover) somem; a linha do tempo,
 * que na tela rola na horizontal, quebra em linhas.
 *
 * Todo texto que vem de dado passa por `esc()`: título de live e nome de
 * produto são digitados por cliente.
 */
import {
  blockIncluded,
  includedSections,
  LiveReportKpi,
  LiveReportModel,
  LiveReportSelection,
  NO_DATA,
  formatInt,
} from './live-report-model'

export function esc(s: string | number | null | undefined): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const TIP_ICON =
  '<svg class="tip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="9"></circle><line x1="12" y1="11" x2="12" y2="16"></line>' +
  '<circle cx="12" cy="7.5" r="0.6" fill="currentColor" stroke="none"></circle></svg>'

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Barlow:wght@700;900&family=Lato:wght@300;400&display=swap');
:root {
  --c-blue: #2667F8;
  --c-blue-deep: #003AB9;
  --c-white: #FFFFFF;
  --c-gray: #F5F5F5;
  --c-black: #000000;
  --c-text-muted: rgba(0,0,0,0.55);
  --c-border: rgba(0,0,0,0.10);
  --c-border-soft: rgba(0,0,0,0.06);
}
* { box-sizing:border-box; }
body { margin:0; background:var(--c-gray); font-family:'Lato',sans-serif; font-weight:400; color:var(--c-black); }
h1,h2,h3 { font-family:'Barlow',sans-serif; font-weight:900; margin:0; letter-spacing:-0.01em; }
.container { max-width:1120px; margin:0 auto; padding:0 40px; }
.section { padding:56px 0; border-top:1px solid var(--c-border); }
.eyebrow { font-family:'Barlow',sans-serif; font-size:12px; font-weight:700; letter-spacing:0.10em; text-transform:uppercase; color:var(--c-blue); margin-bottom:10px; }
.h2 { font-size:24px; margin-bottom:22px; }
.grid { display:grid; gap:14px; }
.kpi-card { background:var(--c-white); border:1px solid var(--c-border); border-radius:20px; padding:20px 22px; }
.kpi-value { font-family:'Barlow',sans-serif; font-weight:900; font-size:30px; line-height:1.05; }
.kpi-label { font-family:'Lato',sans-serif; font-weight:300; font-size:12.5px; color:var(--c-text-muted); margin-top:6px; }
.kpi-value.no-data, .no-data { color:var(--c-text-muted); font-family:'Lato',sans-serif; font-style:italic; font-weight:300; }
.kpi-value.no-data { font-size:15px !important; line-height:1.4; }
table.dtable { width:100%; border-collapse:collapse; font-size:14px; }
table.dtable th { text-align:left; font-weight:400; font-size:11px; text-transform:uppercase; letter-spacing:0.06em; color:var(--c-text-muted); padding:10px 12px; border-bottom:1px solid var(--c-border); }
table.dtable td { padding:12px 12px; border-bottom:1px solid var(--c-border-soft); vertical-align:middle; }
table.dtable .num { text-align:right; white-space:nowrap; }
table.dtable th.num { white-space:normal; }
.table-wrap { overflow-x:auto; }
.pill { display:inline-flex; align-items:center; gap:8px; padding:6px 14px; border-radius:999px; font-family:'Barlow',sans-serif; font-size:11.5px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; background:var(--c-black); color:var(--c-white); }
.live-dot { width:7px; height:7px; border-radius:50%; background:var(--c-blue); display:inline-block; }
.legend-dot { width:10px; height:10px; border-radius:50%; display:inline-block; margin-right:8px; flex-shrink:0; }
.subhead { font-family:'Barlow',sans-serif; font-weight:700; font-size:14px; margin-bottom:16px; display:flex; align-items:center; gap:6px; }
.rank-badge { width:20px; height:20px; border-radius:6px; background:rgba(38,103,248,0.10); color:var(--c-blue); font-family:'Barlow',sans-serif; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.note { font-weight:300; font-style:italic; font-size:11px; color:var(--c-text-muted); }

.tip { position:relative; display:inline-flex; align-items:center; gap:5px; cursor:help; }
.tip-icon { width:13px; height:13px; flex-shrink:0; color:rgba(0,0,0,0.32); transition:color .15s ease; }
.tip:hover .tip-icon { color:var(--c-blue); }
.tip-bubble { visibility:hidden; opacity:0; position:absolute; top:calc(100% + 9px); left:50%; transform:translateX(-50%); background:#000; color:#fff; font-weight:300; font-size:11.5px; line-height:1.45; padding:9px 11px; border-radius:10px; width:200px; text-align:left; transition:opacity .15s ease; z-index:30; pointer-events:none; }
.tip:hover .tip-bubble { visibility:visible; opacity:1; }
.on-dark .tip-icon { color:rgba(255,255,255,0.55); }

.hscroll { overflow-x:auto; scrollbar-width:thin; scrollbar-color:rgba(0,0,0,0.18) transparent; }
.hscroll::-webkit-scrollbar { height:6px; }
.hscroll::-webkit-scrollbar-thumb { background:rgba(0,0,0,0.18); border-radius:999px; }
.timeline { position:relative; display:flex; height:110px; padding-top:4px; }
.timeline-line { position:absolute; left:0; right:0; top:52px; height:2px; background:var(--c-border); }
.timeline-item { width:170px; flex:0 0 170px; text-align:center; position:relative; }
.timeline-time { font-size:11px; color:var(--c-text-muted); margin-bottom:10px; }
.dot-filled { width:18px; height:18px; border-radius:50%; background:var(--c-blue); margin:0 auto; }
.dot-empty { width:12px; height:12px; border-radius:50%; background:#fff; border:2px solid rgba(0,0,0,0.30); margin:3px auto; }
.timeline-name { font-size:12px; margin-top:12px; padding:0 8px; }

.opp-card { border-radius:20px; padding:22px; background:radial-gradient(circle at 28% 22%, rgba(38,103,248,0.55), rgba(0,58,185,0.85) 42%, #000 78%); }
.insight-main { background:var(--c-blue); border-radius:20px; padding:24px 26px; color:#fff; }
.insight-tag { font-family:'Barlow',sans-serif; font-weight:700; font-size:12px; text-transform:uppercase; letter-spacing:0.08em; margin-bottom:8px; }
.insight-text { font-size:15px; line-height:1.5; }

@media screen and (max-width:900px) {
  .container { padding:0 20px; }
  .hero-title { font-size:44px !important; }
  .grid { grid-template-columns:repeat(2, minmax(0,1fr)) !important; }
  .split { grid-template-columns:1fr !important; }
  .funnel-label { width:120px !important; }
  table.dtable td, table.dtable th { padding:10px 8px; }
}

@page { size:A4 portrait; margin:10mm; }
@media print {
  html { zoom:0.62; }
  body { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  .tip-icon, .tip-bubble { display:none !important; }
  .hscroll, .table-wrap { overflow:visible; }
  .timeline { flex-wrap:wrap; height:auto; row-gap:18px; min-width:0 !important; }
  .timeline-line { display:none; }
  .kpi-card, .opp-card, .insight-main, tr, .avoid-break { break-inside:avoid; }
  .eyebrow, .h2, .subhead { break-after:avoid; }
  .section { padding:36px 0; }
}
`

function kpiCard(k: LiveReportKpi, opts: { size?: number; highlight?: boolean } = {}): string {
  const size = opts.size ? ` style="font-size:${opts.size}px;"` : ''
  const value = k.value === null
    ? `<div class="kpi-value no-data">${NO_DATA}</div>`
    : `<div class="kpi-value"${opts.highlight ? ' style="color:#fff;"' : size}>${esc(k.value)}</div>`
  const label = `<div class="kpi-label tip"${opts.highlight ? ' style="color:rgba(255,255,255,0.75);"' : ''}>${esc(k.label)}${k.noted ? '*' : ''}${TIP_ICON}<span class="tip-bubble">${esc(k.tip)}</span></div>`
  const card = opts.highlight ? ' on-dark" style="background:var(--c-blue); border-color:var(--c-blue);' : ''
  return `<div class="kpi-card${card}">${value}${label}</div>`
}

function subhead(title: string, tip?: string): string {
  return tip
    ? `<div class="subhead tip">${esc(title)}${TIP_ICON}<span class="tip-bubble">${esc(tip)}</span></div>`
    : `<div class="subhead">${esc(title)}</div>`
}

function sectionOpen(eyebrow: string, title: string, extraStyle = ''): string {
  return `<div class="section"${extraStyle ? ` style="${extraStyle}"` : ''}><div class="container"><div class="eyebrow">${esc(eyebrow)}</div><h2 class="h2">${esc(title)}</h2>`
}

const SECTION_CLOSE = '</div></div>'

function grid(cols: number, cells: string[], extra = ''): string {
  return `<div class="grid" style="grid-template-columns:repeat(${cols}, minmax(0,1fr));${extra}">${cells.join('')}</div>`
}

function renderHero(m: LiveReportModel): string {
  const [revenue, ...rest] = m.hero.kpis
  return `
<div class="container" style="padding-top:44px; padding-bottom:40px;">
  <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:32px;">
    <div style="font-family:'Barlow',sans-serif; font-weight:900; font-size:19px; letter-spacing:-0.01em;">WIDDE</div>
    <div style="font-size:12px; color:var(--c-text-muted); border:1px dashed var(--c-border); border-radius:8px; padding:6px 12px;">${esc(m.hero.company)}</div>
  </div>
  <div class="pill" style="margin-bottom:22px;"><span class="live-dot"></span>Resultados da live</div>
  <h1 class="hero-title" style="font-size:64px; line-height:0.98; margin-bottom:14px;">${esc(m.hero.title)}</h1>
  <div style="font-size:18px; margin-bottom:16px;">${esc(m.hero.company)}</div>
  <div style="display:flex; gap:20px; flex-wrap:wrap; font-weight:300; font-size:11.5px; letter-spacing:0.04em; text-transform:uppercase; color:var(--c-text-muted); margin-bottom:36px;">
    ${m.hero.meta.map((t) => `<span>${esc(t)}</span>`).join('')}
  </div>
  ${grid(4, [kpiCard(revenue, { highlight: true }), ...rest.map((k) => kpiCard(k))])}
</div>`
}

function renderAudience(m: LiveReportModel, sel: LiveReportSelection): string {
  const showRotation = blockIncluded('audience.rotation', sel, m)
  const showTop = blockIncluded('audience.topViewers', sel, m)
  const rotation = showRotation
    ? `<div style="display:flex; flex-direction:column;">
        ${subhead('Rotação de audiência (tempo assistido)', 'Mostra quantos espectadores ficaram em cada faixa de tempo assistido.')}
        <div class="kpi-card" style="flex:1; padding:22px 24px;"><div style="display:flex; flex-direction:column; gap:11px;">
          ${m.audience.bands.map((b) => `
          <div style="display:flex; align-items:center; gap:10px;">
            <div style="width:66px; font-size:13px; color:var(--c-text-muted); flex-shrink:0;">${esc(b.label)}</div>
            <div style="flex-grow:1; background:var(--c-gray); border-radius:6px; height:20px;"><div style="width:${b.pct}%; background:${b.alpha >= 1 ? 'var(--c-blue)' : `rgba(38,103,248,${b.alpha})`}; border-radius:6px; height:20px;"></div></div>
            <div style="width:96px; font-size:13px; text-align:right; flex-shrink:0;">${esc(b.valueText)}</div>
          </div>`).join('')}
        </div></div>
      </div>`
    : ''
  const top = showTop
    ? `<div style="display:flex; flex-direction:column;">
        ${subhead('Top 5 mais engajados', 'Espectadores que passaram mais tempo assistindo à live.')}
        <div class="kpi-card" style="flex:1; padding:8px 18px;">
          ${m.audience.topViewers.map((v, i, all) => `
          <div style="display:flex; align-items:center; gap:10px; padding:12px 0;${i < all.length - 1 ? ' border-bottom:1px solid var(--c-border-soft);' : ''}">
            <div class="rank-badge">${v.rank}</div>
            <div style="flex-grow:1; font-size:13.5px;">${esc(v.name)}</div>
            <div style="font-size:13px; color:var(--c-text-muted); text-align:right;">${esc(v.detail)}</div>
          </div>`).join('')}
        </div>
      </div>`
    : ''
  const split = rotation && top
    ? `<div class="grid split" style="grid-template-columns:1.5fr 1fr; gap:28px; align-items:stretch; margin-top:8px;">${rotation}${top}</div>`
    : `<div style="margin-top:8px;">${rotation || top}</div>`
  return `${sectionOpen('Audiência & Retenção', 'Quem assistiu e por quanto tempo')}
  ${grid(6, m.audience.kpis.map((k) => kpiCard(k, { size: 21 })), ' gap:12px; margin-bottom:32px;')}
  ${rotation || top ? split : ''}
${SECTION_CLOSE}`
}

/** Rosca em `conic-gradient` (sem JS, imprime igual). */
function donut(channels: LiveReportModel['engagement']['channels']): string {
  const total = channels.reduce((s, c) => s + c.viewers, 0)
  let acc = 0
  const stops = channels.map((c) => {
    const from = total > 0 ? (acc / total) * 360 : 0
    acc += c.viewers
    const to = total > 0 ? (acc / total) * 360 : 0
    return `${c.color} ${from.toFixed(1)}deg ${to.toFixed(1)}deg`
  })
  return `<div style="position:relative; width:150px; height:150px; flex-shrink:0; border-radius:50%; background:conic-gradient(${stops.join(', ')});">
    <div style="position:absolute; top:28px; left:28px; width:94px; height:94px; border-radius:50%; background:var(--c-gray);"></div>
  </div>`
}

function renderEngagement(m: LiveReportModel, sel: LiveReportSelection): string {
  const chs = m.engagement.channels
  const origin = blockIncluded('engagement.origin', sel, m)
    ? `<div class="grid split" style="grid-template-columns:0.9fr 1.4fr; gap:40px;">
        <div class="avoid-break">
          ${subhead('Origem por canal', 'De onde vieram os espectadores que assistiram à live (primeira entrada de cada pessoa).')}
          <div style="display:flex; align-items:center; gap:24px;">
            ${donut(chs)}
            <div style="display:flex; flex-direction:column; gap:7px; font-size:13px;">
              ${chs.map((c) => `<div><span class="legend-dot" style="background:${c.color};"></span>${esc(c.label)} — ${c.pct}%</div>`).join('')}
            </div>
          </div>
        </div>
        <div>
          ${subhead('Origem por canal — espectadores', 'Pessoas que entraram na live a partir de cada canal, contadas pela primeira entrada.')}
          <div class="table-wrap"><table class="dtable"><thead><tr><th>Canal</th><th class="num">Espectadores</th><th class="num">%</th></tr></thead><tbody>
            ${chs.map((c) => `<tr><td>${esc(c.label)}</td><td class="num">${formatInt(c.viewers)}</td><td class="num">${c.pct}%</td></tr>`).join('')}
          </tbody></table></div>
        </div>
      </div>`
    : ''
  return `${sectionOpen('Engajamento & Origem da Audiência', 'Como interagiram e de onde vieram')}
  ${grid(4, m.engagement.kpis.map((k) => kpiCard(k, { size: 24 })), origin ? ' margin-bottom:56px;' : '')}
  ${origin}
${SECTION_CLOSE}`
}

function renderScript(m: LiveReportModel, sel: LiveReportSelection): string {
  const timeline = blockIncluded('script.timeline', sel, m)
    ? `${subhead('Linha do tempo de produtos apresentados')}
      <div class="hscroll" style="margin-bottom:12px;">
        <div class="timeline" style="min-width:${m.script.timeline.length * 170}px;">
          <div class="timeline-line"></div>
          ${m.script.timeline.map((t) => `
          <div class="timeline-item">
            <div class="timeline-time">${esc(t.timeText)}</div>
            <div class="${t.filled ? 'dot-filled' : 'dot-empty'}"></div>
            <div class="timeline-name"${t.filled ? '' : ' style="color:var(--c-text-muted);"'}>${esc(t.name)}</div>
          </div>`).join('')}
        </div>
      </div>
      <div class="note" style="margin-bottom:32px;">${esc(m.script.timelineNote)}</div>`
    : ''
  const top = blockIncluded('script.topProducts', sel, m)
    ? `${subhead('Top produtos por cliques', 'Produtos que mais receberam cliques durante a live.')}
      <div class="table-wrap"><table class="dtable"><thead><tr><th>Produto</th><th class="num">Cliques</th></tr></thead><tbody>
        ${m.script.topProducts.map((p) => `<tr><td>${esc(p.name)}</td><td class="num">${formatInt(p.clicks)}</td></tr>`).join('')}
      </tbody></table></div>`
    : ''
  return `${sectionOpen('Roteiro da Live', 'O que foi apresentado, e quando')}${timeline}${top}${SECTION_CLOSE}`
}

function renderSales(m: LiveReportModel, sel: LiveReportSelection): string {
  const funnel = blockIncluded('sales.funnel', sel, m)
    ? `${subhead('Funil de vendas', 'Caminho do espectador até a compra.')}
      <div class="avoid-break" style="display:flex; flex-direction:column; gap:12px; margin-bottom:36px;">
        ${m.sales.funnel.map((f) => `
        <div style="display:flex; align-items:center; gap:14px;">
          <div class="funnel-label" style="width:210px; font-size:13px; flex-shrink:0;">${esc(f.label)}</div>
          <div style="flex:1;"><div style="width:${f.widthPct}%; min-width:fit-content; background:rgba(38,103,248,${f.alpha.toFixed(2)}); border-radius:10px; padding:12px 14px; color:${f.alpha > 0.6 ? '#fff' : '#0A1E4D'}; font-size:13.5px; white-space:nowrap;">${esc(f.valueText)}</div></div>
        </div>`).join('')}
      </div>`
    : ''
  const cartKpis = blockIncluded('sales.cartKpis', sel, m)
    ? grid(4, m.sales.cartKpis.map((k) => kpiCard(k, { size: 21 })), ' margin-bottom:36px;')
    : ''
  const cartDetail = blockIncluded('sales.cartDetail', sel, m)
    ? `${subhead('Produtos no carrinho — detalhamento', 'Produtos adicionados ao carrinho durante a live por quem assistiu.')}
      <div class="table-wrap"><table class="dtable"><thead><tr><th>Produto</th><th class="num">Unidades no carrinho</th><th class="num">Usuários únicos</th><th class="num">Valor total</th></tr></thead><tbody>
        ${m.sales.cartProducts.map((p) => `<tr><td>${esc(p.name)}</td><td class="num">${formatInt(p.units)}</td><td class="num">${formatInt(p.users)}</td><td class="num">${esc(p.valueText)}</td></tr>`).join('')}
      </tbody></table></div>
      ${m.sales.cartProductsHidden > 0 ? `<div class="note" style="margin-top:10px;">E mais ${formatInt(m.sales.cartProductsHidden)} ${m.sales.cartProductsHidden === 1 ? 'produto' : 'produtos'} com menos unidades.</div>` : ''}`
    : ''
  const breakdown = m.sales.revenueBreakdown.length > 0
    ? grid(m.sales.revenueBreakdown.length, m.sales.revenueBreakdown.map((k) => kpiCard(k, { size: 24 })), ' margin-bottom:36px;')
    : ''
  // Notas sobre os dados: o que os números não garantem (ex.: loja sem GA4).
  const notes = m.sales.dataNotes.length > 0
    ? `<div class="avoid-break" style="margin-top:28px; padding:14px 18px; border:1px solid var(--c-border); border-radius:14px;">
        <div class="subhead" style="margin-bottom:6px;">* Notas sobre os dados</div>
        ${m.sales.dataNotes.map((n) => `<div class="note" style="font-size:12px; margin-top:4px;">${esc(n)}</div>`).join('')}
      </div>`
    : ''
  return `${sectionOpen('Resultados de Vendas', 'O que a live vendeu')}
  ${grid(3, m.sales.kpis.map((k) => kpiCard(k)), m.sales.revenueBreakdown.length > 0 ? ' margin-bottom:16px;' : ' margin-bottom:36px;')}
  ${breakdown}${funnel}${cartKpis}${cartDetail}${notes}
${SECTION_CLOSE}`
}

function cell(n: number | null): string {
  return n === null ? `<span class="no-data">${NO_DATA}</span>` : formatInt(n)
}

function renderOpportunities(m: LiveReportModel, sel: LiveReportSelection): string {
  const cards = m.opportunities.cards.map((k) => `
    <div class="opp-card on-dark">
      ${k.value === null ? `<div class="kpi-value no-data" style="color:rgba(255,255,255,0.75);">${NO_DATA}</div>` : `<div class="kpi-value" style="font-size:26px; color:#fff;">${esc(k.value)}</div>`}
      <div class="kpi-label tip" style="color:rgba(255,255,255,0.72);">${esc(k.label)}${TIP_ICON}<span class="tip-bubble">${esc(k.tip)}</span></div>
    </div>`)
  const gap = blockIncluded('opportunities.gap', sel, m)
    ? `${subhead('Maior gap entre clique e compra')}
      <div class="table-wrap"><table class="dtable"><thead><tr><th>Produto</th><th class="num">Cliques</th><th class="num">No carrinho</th><th class="num">Vendido</th></tr></thead><tbody>
        ${m.opportunities.gap.map((g) => `<tr><td>${esc(g.name)}</td><td class="num">${formatInt(g.clicks)}</td><td class="num">${cell(g.cart)}</td><td class="num">${cell(g.sold)}</td></tr>`).join('')}
      </tbody></table></div>`
    : ''
  return `${sectionOpen('Oportunidades Não Capturadas', 'O que ficou pra trás — e pode ser recuperado')}
  ${grid(2, cards, m.opportunities.note ? ' margin-bottom:10px;' : ' margin-bottom:28px;')}
  ${m.opportunities.note ? `<div class="note" style="margin-bottom:28px;">${esc(m.opportunities.note)}</div>` : ''}
  ${gap}
${SECTION_CLOSE}`
}

function renderInsights(m: LiveReportModel): string {
  return `${sectionOpen('Insights & Próximos Passos', 'O que aprendemos com essa live', 'padding-bottom:60px;')}
  <div class="insight-main">
    <div class="insight-tag" style="color:rgba(255,255,255,0.75);">Recomendação</div>
    <div class="insight-text">${esc(m.insights.main)}</div>
  </div>
  ${m.insights.others.length > 0 ? grid(m.insights.others.length, m.insights.others.map((t) => `
    <div class="kpi-card"><div class="insight-tag" style="color:var(--c-blue);">Leitura</div><div class="insight-text" style="font-size:14px;">${esc(t)}</div></div>`), ' margin-top:14px;') : ''}
${SECTION_CLOSE}`
}

/** Título do documento — o navegador o sugere como nome do PDF salvo. */
export function liveReportDocumentTitle(m: LiveReportModel): string {
  return ['Resultados da live', m.companyTitle, m.liveTitle, m.fileDate].filter(Boolean).join(' · ')
}

export function renderLiveReportHtml(m: LiveReportModel, sel: LiveReportSelection): string {
  const sections = includedSections(sel, m)
  const body = sections
    .map((id) => {
      switch (id) {
        case 'hero': return renderHero(m)
        case 'audience': return renderAudience(m, sel)
        case 'engagement': return renderEngagement(m, sel)
        case 'script': return renderScript(m, sel)
        case 'sales': return renderSales(m, sel)
        case 'opportunities': return renderOpportunities(m, sel)
        case 'insights': return renderInsights(m)
      }
    })
    .join('\n')
  return `<!DOCTYPE html>
<html lang="pt-BR"><head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(liveReportDocumentTitle(m))}</title>
<style>${CSS}</style>
</head>
<body>
${body}
<div class="container" style="padding:24px 40px 48px 40px; font-weight:300; font-size:12px; color:var(--c-text-muted); display:flex; justify-content:space-between; gap:16px;">
  <span>${esc(m.footer.left)}</span>
  <span>${esc(m.footer.right)}</span>
</div>
</body></html>`
}
